import React, { useState, useMemo } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";

const RenewalChart = ({ data = [] }) => {
    const [hovered, setHovered] = useState(null);

    const currentYear = new Date().getFullYear();
    const previousYear = currentYear - 1;

    // Caso não venha dado (ainda carregando), default vazio mas não quebra
    const chartData = data && data.length > 0 ? data : [
        { month: "JAN.", current: 15, previous: 12 },
        { month: "FEV.", current: 22, previous: 18 },
        { month: "MAR.", current: 28, previous: 25 },
        { month: "ABR.", current: 19, previous: 21 },
        { month: "MAI.", current: 16, previous: 14 },
        { month: "JUN.", current: 24, previous: 20 },
        { month: "JUL.", current: 0, previous: 0 },
        { month: "AGO.", current: 0, previous: 0 },
        { month: "SET.", current: 0, previous: 0 },
        { month: "OUT.", current: 0, previous: 0 },
        { month: "NOV.", current: 0, previous: 0 },
        { month: "DEZ.", current: 0, previous: 0 }
    ];

    // Calcula o valor máximo (current ou previous) de todo o dataset para escalonar
    const maxNumber = Math.max(
        ...chartData.map(item => Math.max(item.current || 0, item.previous || 0)),
        1 // Evita divisão por zero
    );
    
    // Deixa uma sobra de 20% pro gráfico não encostar no teto
    const maxValue = maxNumber * 1.2;

    // Gerar ticks dinâmicos e corretos para o eixo Y
    const yTicks = useMemo(() => {
        const step = maxValue / 6;
        const ticks = [];
        for (let i = 6; i >= 0; i--) {
            ticks.push(Math.round(step * i));
        }
        return ticks;
    }, [maxValue]);

    // Definição de cores sólidas e refinadas sem degradê
    const currentColor = "#482712";       // Ano Atual - Marrom outonal sólido
    const previousColor = "#ebdcc5";      // Ano Anterior - Creme/Bege sólido
    const currentHoverColor = "#e2661c";  // Destaque hover Ano Atual - Laranja vibrante sólido

    return (
        <Card 
            className="neo-stat-card h-100" 
            style={{ 
                borderRadius: "16px", 
                border: "2px solid #482712", 
                boxShadow: "0 15px 40px rgba(72, 39, 18, 0.08)",
                background: "#fff",
                fontFamily: "'Outfit', 'Inter', sans-serif"
            }}
        >
            <CardHeader className="neo-chart-card-header">
                <div className="neo-chart-header-container">
                    <div className="d-flex align-items-center gap-2">
                        <div
                            style={{ 
                                width: "38px", 
                                height: "38px", 
                                minWidth: "38px",
                                borderRadius: "10px",
                                background: "rgba(226, 102, 28, 0.08)",
                                border: "1px solid rgba(226, 102, 28, 0.20)",
                                color: "#e2661c",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            <i className="mdi mdi-sync font-size-18"></i>
                        </div>
                        <h5 className="neo-chart-title">
                            Volume de Renovações
                        </h5>
                    </div>
                    <div className="neo-chart-legend-container">
                        <div className="d-flex align-items-center">
                            <span className="rounded-circle" style={{ width: "10px", height: "10px", backgroundColor: "#ebdcc5", border: "1px solid rgba(72, 39, 18, 0.15)", marginRight: "6px" }} />
                            <small style={{ color: "#784b22", fontWeight: "800", fontSize: "0.72rem" }}>{previousYear}</small>
                        </div>
                        <div className="d-flex align-items-center ms-2">
                            <span className="rounded-circle" style={{ width: "10px", height: "10px", backgroundColor: "#482712", border: "1px solid rgba(72, 39, 18, 0.15)", marginRight: "6px" }} />
                            <small style={{ color: "#482712", fontWeight: "800", fontSize: "0.72rem" }}>{currentYear}</small>
                        </div>
                    </div>
                </div>
            </CardHeader>
            <CardBody className="p-4 mt-2">
                <div style={{ overflowX: "auto", overflowY: "hidden", paddingBottom: "10px" }} className="custom-scroll">
                    <div style={{ height: "260px", display: "flex", alignItems: "flex-end" }}>
                        <div style={{ height: "200px", display: "flex", alignItems: "flex-end", position: "relative", gap: "2px", minWidth: "550px", width: "100%" }}>
                        
                        {/* EIXO Y - LABELS DINÂMICAS */}
                        <div 
                            style={{ 
                                position: "absolute", 
                                left: -5, 
                                height: "100%", 
                                display: "flex", 
                                flexDirection: "column", 
                                justifyContent: "space-between", 
                                fontSize: "0.72rem", 
                                color: "#784b22", 
                                fontWeight: "800",
                                textAlign: "right",
                                width: "20px",
                                pointerEvents: "none",
                                paddingBottom: "22px" // Alinhamento com a linha horizontal de base
                            }}
                        >
                            {yTicks.map((tick, tIdx) => (
                                <span key={tIdx}>{tick}</span>
                            ))}
                        </div>

                        {/* CORPO DO GRÁFICO */}
                        <div 
                            className="flex-grow-1 d-flex h-100 ps-4 pt-1" 
                            style={{ 
                                borderBottom: "none",
                                position: "relative"
                            }}
                        >
                            {chartData.map((item, idx) => {
                                const isHovered = hovered === idx;
                                return (
                                    <div 
                                        key={idx} 
                                        className="flex-grow-1 h-100 d-flex flex-column align-items-center px-1"
                                        style={{ position: "relative", cursor: "pointer", justifyContent: "flex-end" }}
                                        onMouseEnter={() => setHovered(idx)}
                                        onMouseLeave={() => setHovered(null)}
                                    >
                                        {/* TOOLTIP PREMIUM FLUTUANTE */}
                                        {hovered === idx && (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: "-35px",
                                                    left: "50%",
                                                    transform: "translateX(-50%)",
                                                    backgroundColor: "#fff",
                                                    border: "2px solid #482712",
                                                    borderRadius: "8px",
                                                    padding: "8px 12px",
                                                    zIndex: 10,
                                                    whiteSpace: "nowrap",
                                                    fontSize: "0.75rem",
                                                    boxShadow: "0 8px 24px rgba(72, 39, 18, 0.15)",
                                                    pointerEvents: "none"
                                                }}
                                            >
                                                <div style={{ color: "#784b22", fontWeight: "900", textTransform: "uppercase", fontSize: "0.68rem", marginBottom: "4px" }}>
                                                    {item.month}
                                                </div>
                                                <div className="d-flex align-items-center gap-2" style={{ color: "#784b22", fontWeight: "800" }}>
                                                    <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#ebdcc5" }} />
                                                    Anterior: {item.previous || 0}
                                                </div>
                                                <div className="d-flex align-items-center gap-2" style={{ color: "#e2661c", fontWeight: "800" }}>
                                                    <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#e2661c" }} />
                                                    Atual: {item.current || 0}
                                                </div>
                                            </div>
                                        )}

                                        {/* GRUPO DE COLUNAS ANTERIOR/ATUAL */}
                                        <div className="d-flex h-100 align-items-end" style={{ gap: "4px", paddingBottom: "2px" }}>
                                            {/* Barra do Ano Anterior */}
                                            <div 
                                                style={{ 
                                                    width: "14px", 
                                                    height: `${Math.max((item.previous / maxValue) * 100, 2)}%`, 
                                                    background: previousColor, 
                                                    border: "1px solid rgba(72, 39, 18, 0.15)",
                                                    borderRadius: "6px 6px 0 0",
                                                    transition: "height 0.8s cubic-bezier(0.16, 1, 0.3, 1), background 0.2s"
                                                }} 
                                            />
                                            {/* Barra do Ano Atual */}
                                            <div 
                                                style={{ 
                                                    width: "14px", 
                                                    height: `${Math.max((item.current / maxValue) * 100, 2)}%`, 
                                                    background: isHovered ? currentHoverColor : currentColor, 
                                                    border: "1px solid #482712",
                                                    borderRadius: "6px 6px 0 0",
                                                    transition: "height 0.8s cubic-bezier(0.16, 1, 0.3, 1), background 0.2s",
                                                    boxShadow: isHovered ? "0 4px 10px rgba(226, 102, 28, 0.2)" : "none"
                                                }} 
                                            />
                                        </div>
                                        
                                        {/* Mês no eixo X */}
                                        <span 
                                            style={{ 
                                                marginTop: "8px", 
                                                fontSize: "0.68rem", 
                                                fontWeight: "800", 
                                                color: isHovered ? "#e2661c" : "#482712",
                                                textTransform: "uppercase",
                                                transition: "color 0.2s",
                                                lineHeight: "1"
                                            }}
                                        >
                                            {item.month}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
            </CardBody>
        </Card>
    );
};

export default RenewalChart;
